import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight,
  Phone,
  DollarSign,
  Clock,
  Globe,
  Headphones,
  BarChart3,
  Shield,
  CheckCircle,
  Play,
  Wrench,
  Thermometer,
  Home as HomeIcon,
  Zap,
  Sun,
  Bug,
  Truck,
  Plane,
  Search,
  Target,
  TrendingUp,
  Users,
  Monitor,
  Mail,
  Video,
  MessageSquare
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import RunningOffersBanner from "../components/RunningOffersBanner";
import OfferImagesRail from "../components/OfferImagesRail";
import HomeServicesGlobe from "../components/HomeServicesGlobe";
import OffersGalaxy from "../components/OffersGalaxy";
import { CallIntellShowcase } from "../components/CallIntellShowcase";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Stats data
const stats = [
  { value: "$515", label: "Max Payout", suffix: "" },
  { value: "100+", label: "Offers", suffix: "" },
  { value: "$50", label: "Min Payout", suffix: "" },
  { value: "120 sec", label: "Post-IVR Buffer", suffix: "" },
  { value: "Net 7-10", label: "Payment Terms", suffix: "" },
  { value: "USA", label: "Nationwide", suffix: "" }
];

// Services/Verticals
const services = [
  { 
    icon: Phone, 
    title: "High-Converting Calls", 
    desc: "Connect with buyers actively searching for home services. Our calls convert at industry-leading rates.",
    color: "blue"
  },
  { 
    icon: DollarSign, 
    title: "Competitive Payouts", 
    desc: "Earn up to $515 per qualified call with our premium payout structure across all verticals.",
    color: "purple"
  },
  { 
    icon: BarChart3, 
    title: "Real-Time Analytics", 
    desc: "Track your campaigns with live dashboards. Monitor calls, conversions, and earnings instantly.",
    color: "cyan"
  },
  { 
    icon: Headphones, 
    title: "Dedicated Support", 
    desc: "Get personalized guidance from our expert team to optimize your campaigns and maximize ROI.",
    color: "green"
  }
];

// Traffic Sources
const trafficSources = [
  { name: "Google Ads", icon: Search, desc: "Search, Display, and Performance Max campaigns for high-intent leads" },
  { name: "Meta Ads", icon: Users, desc: "Facebook & Instagram ads targeting homeowners and service seekers" },
  { name: "Facebook Marketplace", icon: MessageSquare, desc: "Local marketplace listings and lead gen forms" },
  { name: "SEO / Organic", icon: TrendingUp, desc: "Organic search traffic from optimized landing pages" },
  { name: "Native Ads", icon: Monitor, desc: "Taboola, Outbrain, and other native platforms" },
  { name: "TikTok Ads", icon: Video, desc: "Short-form video ads for younger demographics" },
  { name: "Bing Ads", icon: Search, desc: "Microsoft Advertising for additional search volume" },
  { name: "Email Marketing", icon: Mail, desc: "Permission-based email campaigns to opt-in lists" }
];

// Verticals with PDF rates
const verticals = [
  { name: "Plumbers", icon: Wrench, payout: "Up to $98.50" },
  { name: "HVAC", icon: Thermometer, payout: "Up to $94.00" },
  { name: "Roofers", icon: HomeIcon, payout: "Up to $178.00" },
  { name: "Electricians", icon: Zap, payout: "Up to $57.00" },
  { name: "Water & Fire Damage", icon: Shield, payout: "Up to $515.00" },
  { name: "Solar", icon: Sun, payout: "Up to $53.00" },
  { name: "Pest Control", icon: Bug, payout: "Up to $56.00" },
  { name: "Flight Booking", icon: Plane, payout: "$20.00 Fixed" },
  { name: "Movers", icon: Truck, payout: "Up to $23.00" },
  { name: "Heating", icon: Thermometer, payout: "Up to $96.00" }
];

// Features
const features = [
  "100+ high-converting home services offers",
  "Fast weekly payments (Net 7-10)",
  "$50 minimum payment threshold",
  "120-second post-IVR buffer",
  "Dedicated account manager"
];

// New Dashboard Images
const callDashboardImage = "https://static.prod-images.emergentagent.com/jobs/90387c61-6bb9-4b07-84a1-5d5f34a11d85/images/63c26c9ae46f7b8d9dbba3b8caafaa54a392ce1c050dd517523b012a8bd063aa.png";

// Home services images
const plumbingImage = "https://static.prod-images.emergentagent.com/jobs/90387c61-6bb9-4b07-84a1-5d5f34a11d85/images/8cc53457d14a464099d1595da3460e2e71e99bf795a47bf5c3afb116643df790.png";
const roofingImage = "https://static.prod-images.emergentagent.com/jobs/90387c61-6bb9-4b07-84a1-5d5f34a11d85/images/5fc0da76154ae987bc1268e75c842772c73a097b4246efc9594a9660b19ecbde.png";

const Homepage = () => {
  return (
    <div className="min-h-screen bg-slate-900 font-['Inter']" data-testid="homepage">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] overflow-hidden pt-20 sm:pt-24 lg:pt-28">
        {/* Decorative elements */}
        <div className="absolute top-40 left-10 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-3 sm:px-4 pt-6 pb-16 lg:py-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-8 lg:gap-8 items-center">
            {/* Left Content — appears second on mobile, first on desktop */}
            <motion.div 
              initial="hidden" 
              animate="visible" 
              variants={stagger}
              className="text-center lg:text-left order-2 lg:order-1"
            >
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/10">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-white/80 text-sm font-medium tracking-wide">Digital Master Media LLC · Trusted Pay-Per-Call Network</span>
              </motion.div>
              
              <motion.h1
                variants={fadeUp}
                className="font-['Outfit'] font-extrabold text-white mb-5 leading-[0.94] tracking-[-0.035em] text-[2.4rem] sm:text-[3.5rem] lg:text-[2.85rem] xl:text-[3.5rem] 2xl:text-[4.1rem]"
              >
                <span className="block">Digital Master Media</span>
                <span className="gradient-text block mt-1">Pay-Per-Call Network</span>
              </motion.h1>
              
              <motion.p variants={fadeUp} className="text-gray-300 text-base sm:text-lg mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                A performance-driven call marketing network built for serious publishers. We connect vetted media partners with premium U.S. advertisers across insurance, home services, finance and healthcare — turning every inbound call into measurable revenue. <span className="text-white font-semibold">100+ live offers</span> with payouts up to <span className="text-white font-semibold">$515 per qualified call</span>.
              </motion.p>
              
              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link 
                  to="/apply" 
                  data-testid="hero-publisher-btn"
                  className="gradient-btn text-white font-bold px-8 py-4 rounded-full inline-flex items-center justify-center gap-2"
                >
                  Publisher Sign Up <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/buyer"
                  data-testid="hero-buyer-btn" 
                  className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-bold px-8 py-4 rounded-full inline-flex items-center justify-center gap-2 border border-white/20 transition-all"
                >
                  <Play className="w-5 h-5" /> Buyer Sign Up
                </Link>
              </motion.div>
            </motion.div>
            
            {/* Right - Dashboard Images — appears first on mobile, second on desktop */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative mt-4 lg:mt-0 order-1 lg:order-2"
            >
              {/* Mobile-only headline — 2 lines, edge-to-edge, Outfit font */}
              <div className="lg:hidden mb-6 text-center -mx-2">
                <h2 className="font-['Outfit'] font-extrabold text-white leading-[1.02] tracking-[-0.03em] text-[2.35rem] sm:text-[3rem]">
                  Built for <span className="gradient-text">Publishers.</span><br />
                  Trusted by <span className="gradient-text">Buyers.</span>
                </h2>
              </div>

              {/* Mobile-only galaxy of offers */}
              <div className="lg:hidden">
                <OffersGalaxy />
              </div>

              <div className="relative hidden lg:block">
                {/* Main Dashboard Image */}
                <div className="relative z-10 rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl lg:animate-float border border-white/10">
                  <img 
                    src={callDashboardImage} 
                    alt="Call Tracking Dashboard" 
                    className="w-full h-auto"
                  />
                </div>
                
                {/* Floating Stats Card — hidden on mobile, absolute on desktop */}
                <div className="hidden lg:flex absolute -left-8 top-20 bg-white rounded-2xl p-4 shadow-xl animate-float-delayed z-20">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Max Payout</div>
                      <div className="text-xl font-bold text-gray-900">$515</div>
                    </div>
                  </div>
                </div>
                
                {/* Floating Offers Card */}
                <div className="hidden lg:flex absolute -right-4 bottom-20 bg-white rounded-2xl p-4 shadow-xl animate-float z-20">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Phone className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Active Offers</div>
                      <div className="text-xl font-bold text-gray-900">100+</div>
                    </div>
                  </div>
                </div>
                
                {/* Floating Min Payment Card */}
                <div className="hidden lg:flex absolute left-4 bottom-8 bg-white rounded-2xl p-4 shadow-xl animate-float-delayed z-20">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Min Payment Threshold</div>
                      <div className="text-xl font-bold text-gray-900">$50</div>
                    </div>
                  </div>
                </div>
                
                {/* Floating Buffer Time Card */}
                <div className="hidden lg:flex absolute right-8 top-8 bg-white rounded-2xl p-4 shadow-xl animate-float z-20">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Post-IVR Buffer</div>
                      <div className="text-xl font-bold text-gray-900">120 sec</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile stat grid — replaces the desktop floating cards */}
              <div className="grid grid-cols-2 gap-3 mt-4 lg:hidden">
                <div className="bg-white/95 backdrop-blur rounded-xl p-3 shadow-lg flex items-center gap-2.5">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-green-600" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Max Payout</div>
                    <div className="text-base font-bold text-gray-900 leading-tight">$515</div>
                  </div>
                </div>
                <div className="bg-white/95 backdrop-blur rounded-xl p-3 shadow-lg flex items-center gap-2.5">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-blue-600" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Active Offers</div>
                    <div className="text-base font-bold text-gray-900 leading-tight">100+</div>
                  </div>
                </div>
                <div className="bg-white/95 backdrop-blur rounded-xl p-3 shadow-lg flex items-center gap-2.5">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-purple-600" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Min Payout</div>
                    <div className="text-base font-bold text-gray-900 leading-tight">$50</div>
                  </div>
                </div>
                <div className="bg-white/95 backdrop-blur rounded-xl p-3 shadow-lg flex items-center gap-2.5">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-amber-600" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Post-IVR</div>
                    <div className="text-base font-bold text-gray-900 leading-tight">120 sec</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Running Offers Banner */}
      <RunningOffersBanner />

      {/* Offer Images Rail */}
      <OfferImagesRail />

      {/* Services Section */}
      <section className="py-20 bg-slate-800" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.span variants={fadeUp} className="inline-block text-blue-400 font-semibold text-sm uppercase tracking-wider mb-4">
              What We Offer
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              We Provide Amazing <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Digital Solutions</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg max-w-2xl mx-auto">
              Connect with high-intent customers through our premium pay-per-call network designed for home services
            </motion.p>
          </motion.div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="service-card bg-slate-900/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 hover:border-blue-500/50 transition-all"
                data-testid={`service-card-${index}`}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${
                  service.color === 'blue' ? 'bg-blue-500/20' :
                  service.color === 'purple' ? 'bg-purple-500/20' :
                  service.color === 'cyan' ? 'bg-cyan-500/20' : 'bg-green-500/20'
                }`}>
                  <service.icon className={`w-7 h-7 ${
                    service.color === 'blue' ? 'text-blue-400' :
                    service.color === 'purple' ? 'text-purple-400' :
                    service.color === 'cyan' ? 'text-cyan-400' : 'text-green-400'
                  }`} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-slate-400 leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About/Features Section */}
      <section className="py-20 bg-slate-900" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Animated globe — shows home service offers orbiting a rotating globe */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <HomeServicesGlobe />

              {/* Decorative element */}
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl -z-10"></div>
            </motion.div>
            
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block text-blue-400 font-semibold text-sm uppercase tracking-wider mb-4">
                About Our Network
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Your Trusted Partner for <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Home Services Leads</span>
              </h2>
              <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                Digital Master Media specializes in connecting home services businesses with customers who need immediate assistance. Our pay-per-call model ensures you only pay for real conversations with interested prospects.
              </p>
              
              <ul className="space-y-4 mb-8">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-blue-400" />
                    </div>
                    <span className="text-slate-300">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/about" 
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-full transition-all"
                >
                  Learn More
                </Link>
                <Link 
                  to="/offers" 
                  className="bg-slate-800 border-2 border-slate-700 hover:border-blue-500 text-slate-300 hover:text-white font-semibold px-6 py-3 rounded-full transition-all"
                >
                  View All PPC Offers
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Traffic Sources Section */}
      <section className="py-20 bg-slate-900 relative overflow-hidden" data-testid="traffic-sources-section">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-transparent to-purple-600/5"></div>
        <div className="max-w-7xl mx-auto px-4 relative">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.span variants={fadeUp} className="inline-block text-blue-400 font-semibold text-sm uppercase tracking-wider mb-4">
              Accepted Traffic Sources
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Multiple Ways to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Generate Calls</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 max-w-2xl mx-auto">
              We accept quality traffic from various sources. Choose the platforms that work best for your strategy.
            </motion.p>
          </motion.div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {trafficSources.map((source, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="group bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 hover:bg-slate-800 transition-all border border-slate-700 hover:border-blue-500/50"
                data-testid={`traffic-source-${source.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-blue-500/25 transition-all">
                  <source.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-white text-lg mb-2 group-hover:text-blue-400 transition-colors">{source.name}</h3>
                <p className="text-sm text-slate-400">{source.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Verticals Grid */}
      <section className="py-20 bg-slate-800" data-testid="verticals-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-12"
          >
            <motion.span variants={fadeUp} className="inline-block text-blue-400 font-semibold text-sm uppercase tracking-wider mb-4">
              Our Verticals
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white mb-4">
              High-Converting <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Offers</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 max-w-2xl mx-auto">
              Choose from 100+ home services verticals with competitive payouts
            </motion.p>
          </motion.div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {verticals.map((vertical, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="group bg-slate-900/50 backdrop-blur-sm rounded-xl p-5 border border-slate-700 hover:border-blue-500/50 hover:bg-slate-900 transition-all cursor-pointer"
                data-testid={`vertical-${vertical.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center mb-4 group-hover:from-blue-500/30 group-hover:to-purple-500/30 transition-all">
                  <vertical.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-semibold text-white mb-1">{vertical.name}</h3>
                <p className="text-green-400 font-bold text-sm">{vertical.payout}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Link 
              to="/offers" 
              className="inline-flex items-center gap-2 text-blue-400 font-semibold hover:gap-3 hover:text-blue-300 transition-all"
              data-testid="view-all-offers-link"
            >
              View All 100+ PPC Offers <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] text-white relative overflow-hidden" data-testid="how-it-works-section">
        {/* Decorative */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.span variants={fadeUp} className="inline-block text-blue-400 font-semibold text-sm uppercase tracking-wider mb-4">
              Get Started
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              How It <span className="text-blue-400">Works</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-300 max-w-2xl mx-auto">
              Start earning in three simple steps
            </motion.p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Apply", desc: "Fill out our publisher application form with your details and traffic sources" },
              { step: "02", title: "Get Approved", desc: "Our team reviews your application and sets up your account within 24-48 hours" },
              { step: "03", title: "Start Earning", desc: "Choose offers, generate quality calls, and earn competitive payouts" }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative text-center"
                data-testid={`step-${item.step}`}
              >
                <div className="text-7xl font-bold text-blue-500/20 mb-4">{item.step}</div>
                <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
                
                {index < 2 && (
                  <div className="hidden md:block absolute top-12 right-0 translate-x-1/2 w-16 h-0.5 bg-gradient-to-r from-blue-500/50 to-transparent"></div>
                )}
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/apply" 
              className="gradient-btn text-white font-bold px-10 py-4 rounded-full inline-flex items-center gap-2"
              data-testid="apply-now-btn"
            >
              Publisher Sign Up <ArrowRight className="w-5 h-5" />
            </Link>
            <Link 
              to="/buyer" 
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-bold px-10 py-4 rounded-full inline-flex items-center gap-2 border border-white/20 transition-all"
            >
              Buyer Sign Up <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Boost Your Revenue?
            </h2>
            <p className="text-white/90 text-lg mb-10 max-w-2xl mx-auto">
              Join hundreds of publishers already earning with Digital Master Media's premium pay-per-call network
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/apply" 
                className="bg-white text-blue-600 hover:bg-gray-100 font-bold px-10 py-4 rounded-full inline-flex items-center justify-center gap-2 transition-all"
                data-testid="cta-publisher-btn"
              >
                Publisher Sign Up <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/buyer" 
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-bold px-10 py-4 rounded-full inline-flex items-center justify-center gap-2 border border-white/30 transition-all"
                data-testid="cta-buyer-btn"
              >
                Buyer Sign Up
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <CallIntellShowcase />

      <Footer />
    </div>
  );
};

export default Homepage;
